import { FC, useState, useContext, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { Button, Input, Select, SelectOptionType, useToast, Checkbox } from '@aidkitorg/component-library';
import { usePost } from '../../../../API';
import { langToWord, supportedLanguages, useLocalizedStrings } from '../../../../Localization';
import { SupportedLanguage, PublicConfigurationContext } from '../../../../Context';
import { useUserAllowed } from '../../../../Components/PermissionsUtil';

export interface ToolsTabProps {
  supportCaseId: string;
  lang?: SupportedLanguage;
  verified?: string[];
  channel?: string;
  parentContact: string;
  onResolveParentCase?: () => Promise<void>;
}

export const ToolsTab: FC<ToolsTabProps> = ({
  supportCaseId,
  lang: initialLang,
  verified,
  channel,
  parentContact,
  onResolveParentCase
}) => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const publicConfig = useContext(PublicConfigurationContext);
  const [email, setEmail] = useState('');
  const sendLinkVerificationAPI = usePost('/confirm/send_link');
  const { toast } = useToast();

  // Create new support case state
  const [contact, setContact] = useState('');
  const [resolved, setResolved] = useState(false);
  const [showCreateCaseError, setShowCreateCaseError] = useState(false);
  const createCaseAPI = usePost('/support/create_case');

  // Language options with proper typing
  const languageOptions: SelectOptionType[] = Object.entries(supportedLanguages).map(([key, value]) => ({
    label: `${value}${key !== 'en' ? ' (' + langToWord(key as SupportedLanguage, 'Name') + ')' : ''}`,
    value: key,
  }));

  // Find the initial language option
  const initialLangOption = languageOptions.find(opt => opt.value === (initialLang || 'en')) || languageOptions[0];
  const [selectedLang, setSelectedLang] = useState<SelectOptionType>(initialLangOption);

  // Send verification link mutation
  const sendVerificationMutation = useMutation({
    mutationFn: async ({ email, lang }: { email: string; lang: SupportedLanguage }) => {
      return await sendLinkVerificationAPI({
        supportCaseId,
        kind: 'email',
        email,
        lang,
      });
    },
    onSuccess: (response) => {
      if (response?.status) {
        toast({
          description: L.applicant.comms.sent,
          variant: 'success',
        });
        setEmail('');
      }
    },
    onError: () => {
      toast({
        description: L.mfa_link.failed_to_send,
        variant: 'error',
      });
    },
  });

  const handleSendVerification = () => {
    // Basic email validation
    const emailPattern = /^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/;
    if (!emailPattern.test(email)) {
      toast({
        description: L.mfa_link.invalid_email_address,
        variant: 'error',
      });
      return;
    }

    sendVerificationMutation.mutate({
      email,
      lang: selectedLang.value as SupportedLanguage,
    });
  };

  // Create new support case validation
  const getErrors = (contact: string) => {
    if (!contact || !contact.match(/^\d{10}$/)) {
      return {
        hasError: true,
        errorMessage: L.support.please_enter_valid_number
      };
    }

    if (parentContact === contact) {
      return {
        hasError: true,
        errorMessage: L.support.the_number_you_entered
      };
    }

    return undefined;
  };

  // Create support case mutation
  const createSupportCaseMutation = useMutation({
    mutationFn: async ({ contact, channel }: { contact: string; channel: string }) => {
      return await createCaseAPI({
        channel,
        contact
      });
    },
    onSuccess: (response) => {
      if (response?.supportCase) {
        // Navigate to the new support case
        history.push(`/admin-panel/support/${response.supportCase.uid}`);
      }
    },
    onError: () => {
      toast({
        description: L.support.failed_to_create_support_case,
        variant: 'error',
      });
    },
  });

  // Handle create support case
  const handleCreateSupportCase = useCallback(async () => {
    const errors = getErrors(contact);

    if (errors) {
      setShowCreateCaseError(true);
      return;
    }

    if (resolved && onResolveParentCase) {
      await onResolveParentCase();
    }

    createSupportCaseMutation.mutate({
      channel: channel || 'default',
      contact
    });
  }, [contact, resolved, onResolveParentCase, channel, getErrors]);

  // Permission check for creating support cases
  const canCreateSupportCase = useUserAllowed(
    (channel === 'default' || !channel ?
      publicConfig?.comms?.default?.customSupportCaseCreation?.limitedToTags
      : publicConfig?.comms?.otherChannels.find(c => c.channelName === channel)?.customSupportCaseCreation?.limitedToTags) || ['not allowed']
  );

  const createSupportCaseEnabled = publicConfig.comms?.default.customSupportCaseCreation?.enabled;

  return (
    <div className="flex flex-col space-y-4">
      <div className="pb-2 flex flex-col space-y-4">
        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-0">{L.mfa_link.verified_contacts}</h3>
        {verified && verified.length > 0 && (
          <div>
            <ul className="list-disc pl-3 space-y-1 mb-0">
              {verified.map((v) => (
                <li key={v} className="text-sm text-gray-700">
                  {v}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <Input
            id="email"
            label={L.mfa_link.enter_an_email_to_verify}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            description={L.mfa_link.we_will_send_an_email}
            className="w-full"
          />
        </div>

        <div>
          <Select
            label={L.mfa_link.select_a_language}
            description=""
            value={selectedLang}
            onChange={(value) => setSelectedLang(value)}
            options={languageOptions}
            className="w-full"
            searchable={true}
          />
        </div>

        <Button
          onClick={handleSendVerification}
          className="w-full"
          disabled={!email.trim() || sendVerificationMutation.isPending}
        >
          {L.mfa_link.send_verification_link}
        </Button>
      </div>

      {/* Create New Support Case Section */}
      {canCreateSupportCase && createSupportCaseEnabled && (
        <div className="pt-4 border-t border-gray-200 flex flex-col space-y-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-0">{L.support.create_a_new_support_case}</h3>
          <Input
            label={L.support.contact}
            required
            value={contact}
            onChange={(e) => setContact(e.target.value + '')}
            formatPattern="(+1) ### ### ####"
            error={showCreateCaseError ? getErrors(contact) : undefined}
          />
          <Input
            label={"Extension (Not yet implemented)"}
            disabled={true}
            onChange={(e) => setContact(e.target.value)}
            formatPattern="####"
          />
          <Checkbox
            label={L.support.mark_this_as_resolved}
            checked={resolved}
            onChange={(e) => setResolved(typeof e === 'boolean' ? e : e.target.checked)}
          />
          <Button
            disabled={createSupportCaseMutation.isPending}
            onClick={handleCreateSupportCase}
            className="w-full"
          >
            {L.support.create}
          </Button>
        </div>
      )}
    </div>
  );
};
